"use client";

import {
  CommandGroup,
  CommandSeparator,
} from "@universe/ui/components/command";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";

import { useSettlementMethods } from "@/hooks/useSettlementMethods";
import { useCustomerAccount, useGlobalData } from "@/providers";
import { compareCurrencyRank, isUsdStablecoin } from "@/utils/sortProducts";

import { AccountSelectItem } from "./account-select-item";
import { AccountSelectPopover } from "./account-select-popover";

type SettlementAccountsSelectProps = {
  className?: string;
  placeholder?: string;
  currentAccountId?: string;
  onSelect?: (accountId: string) => void;
  showFlag?: boolean;
  flagSize?: string;
  popoverAlign?: "start" | "center" | "end";
};

type SettlementAccountItem = {
  value: string;
  label: string;
  name: string;
  type: "currencies" | "digital-assets";
  searchIndex: string;
};

/**
 * Account select component for the deposit details page.
 * Shows accounts that have settlement methods (deposit options) available.
 */
export function SettlementAccountsSelect({
  className,
  placeholder,
  currentAccountId,
  onSelect,
  showFlag = false,
  flagSize = "28px",
  popoverAlign = "start",
}: SettlementAccountsSelectProps) {
  const tAccounts = useTranslations("accounts");
  const [open, setOpen] = useState(false);
  // Use local state only for user selections, prefer currentAccountId from props
  const [localValue, setLocalValue] = useState("");
  // Derive value: prefer prop from parent, fallback to local selection
  const value = currentAccountId ?? localValue;

  const { fiat, blockchain, isLoading } = useSettlementMethods();
  const { products } = useGlobalData();
  const { customerAccountBaseCurrency } = useCustomerAccount();

  // Build list of fiat accounts from settlement methods
  const fiatAccountsList = useMemo(() => {
    // Group by currency to avoid duplicates
    const currencyMap = new Map<string, SettlementAccountItem>();

    fiat.forEach((method) => {
      if (!currencyMap.has(method.currency)) {
        const product = products?.find((p) => p.code === method.currency);
        currencyMap.set(method.currency, {
          value: method.currency,
          label: method.currency,
          name: product?.name || method.currency,
          type: "currencies",
          searchIndex: `${method.currency}-${product?.name || method.currency}`,
        });
      }
    });

    // Sort with base currency first, then by currency rank (fiat majors first)
    return Array.from(currencyMap.values()).sort((a, b) => {
      if (a.value === customerAccountBaseCurrency) return -1;
      if (b.value === customerAccountBaseCurrency) return 1;
      return compareCurrencyRank(
        { code: a.value, type: a.type },
        { code: b.value, type: b.type }
      );
    });
  }, [fiat, products, customerAccountBaseCurrency]);

  // Build list of blockchain accounts from settlement methods
  const blockchainAccountsList = useMemo(() => {
    // Group by product to avoid duplicates
    const productMap = new Map<string, SettlementAccountItem>();

    blockchain.forEach((method) => {
      if (!productMap.has(method.product)) {
        const product = products?.find((p) => p.code === method.product);
        productMap.set(method.product, {
          value: method.product,
          label: method.product,
          name: product?.name || method.product,
          type: "digital-assets",
          searchIndex: `${method.product}-${product?.name || method.product}`,
        });
      }
    });

    // Sort with USD stablecoins first, then alphabetically
    return Array.from(productMap.values()).sort((a, b) => {
      const aIsUsd = isUsdStablecoin(a.value);
      const bIsUsd = isUsdStablecoin(b.value);
      if (aIsUsd && !bIsUsd) return -1;
      if (!aIsUsd && bIsUsd) return 1;
      return a.value.localeCompare(b.value);
    });
  }, [blockchain, products]);

  const allAccountsList = useMemo(
    () => [...fiatAccountsList, ...blockchainAccountsList],
    [fiatAccountsList, blockchainAccountsList]
  );

  const selectedAccount = allAccountsList.find(
    (account) => account.value === value
  );

  const handleSelect = (accountId: string) => {
    // Close popover first to prevent re-render issues during parent state updates
    setOpen(false);
    setLocalValue(accountId);
    onSelect?.(accountId);
  };

  return (
    <AccountSelectPopover
      open={open}
      onOpenChange={setOpen}
      isLoading={isLoading}
      className={className}
      popoverAlign={popoverAlign}
      selectedValue={selectedAccount?.value}
      selectedName={selectedAccount?.name}
      showFlag={showFlag}
      flagSize={flagSize}
      placeholder={placeholder}
    >
      {/* Fiat Accounts Group */}
      {fiatAccountsList.length > 0 && (
        <>
          <CommandGroup>
            <div className="px-3 py-2 text-base font-semibold text-gray-700">
              {tAccounts("fiatAccounts")}
            </div>
            {fiatAccountsList.map((account) => (
              <AccountSelectItem
                key={account.searchIndex}
                value={account.value}
                label={account.label}
                name={account.name}
                searchIndex={account.searchIndex}
                isSelected={value === account.value}
                onSelect={() => handleSelect(account.value)}
              />
            ))}
          </CommandGroup>
          {blockchainAccountsList.length > 0 && <CommandSeparator />}
        </>
      )}

      {/* Blockchain Accounts Group */}
      {blockchainAccountsList.length > 0 && (
        <CommandGroup>
          <div className="px-3 py-2 text-base font-semibold text-gray-700">
            {tAccounts("blockchainAccounts")}
          </div>
          {blockchainAccountsList.map((account) => (
            <AccountSelectItem
              key={account.searchIndex}
              value={account.value}
              label={account.label}
              name={account.name}
              searchIndex={account.searchIndex}
              isSelected={value === account.value}
              onSelect={() => handleSelect(account.value)}
            />
          ))}
        </CommandGroup>
      )}
    </AccountSelectPopover>
  );
}
